import React, { useEffect, useState } from "react"
import { Box, Text, Flex, Button, FieldLabel, AbsoluteCenter, Separator, Link, HStack, Center } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { useGetPref, useSetPref } from "@hydrologiq/shared/providers/helpers/usePref"
import { Figure, Graph, Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"
import { rangeInMS } from "@hydrologiq/shared/monitoring/builders/1.0.0/graphs/rangeFilter"
import { dateDiffInDays, IANATimeZone, nowDateTime, toUserTZ } from "@hydrologiq/common/helpers/date"
import {
  LAST_12_HOURS_SCALE,
  LAST_MONTHS_SCALE,
  LAST_MONTH_SCALE,
  LAST_WEEKS_SCALE,
  LAST_WEEK_SCALE,
} from "@hydrologiq/shared/monitoring/builders/1.0.0/graphs/timeScales"
import assetHydrogenGraphs from "../asset/monitoring/graphs/AssetHydrogen.json"
import assetHydrogenFigures from "../asset/monitoring/figures/AssetHydrogen.json"
import Figures from "@hydrologiq/shared/monitoring/builders/1.0.0/Figures"
import InstallationHeadlineFigures from "./InstallationHeadlineFigures"

import "./InstallationMonitoringView.scss"
import { TimePeriodMappings, TimePeriod } from "../monitoring/builders/1.0.0/types/timeperiod"
import Graphs from "../monitoring/builders/2.0.0/Graphs"
import { days } from "timezonecomplete"
import { AssetConfig } from "../monitoring/builders/2.0.0/graphs/TimeSeries"
import { Asset } from "@hydrologiq/gql-experience/graphql"
import { AssetMonitoring } from "@hydrologiq/gql/graphql"
interface InstallationMonitoringViewProps {
  installation?: string
  installationStartDate: string
  installationEndDate?: string
  asset?: string
  figures?: Figure[]
  graphs?: Graph[]
  assetConfigs?: AssetConfig[]
  assetContext?: Asset
  assetMonitoring?: AssetMonitoring
  onRefresh?: () => void
  useNewHeadlines?: boolean
}

const mapToOption = (period: TimePeriod) => {
  return { label: period, value: Object.keys(TimePeriod)[Object.values(TimePeriod).indexOf(period)] }
}

const mapToOptions = (periods: [string, TimePeriod][]) =>
  periods.map(([key, label]) => {
    return { label, value: key }
  })

const InstallationMonitoringView: React.FC<InstallationMonitoringViewProps> = ({
  installation,
  installationStartDate,
  installationEndDate,
  asset,
  figures: pFigures = assetHydrogenFigures,
  graphs: pGraphs = assetHydrogenGraphs,
  assetConfigs,
  assetContext,
  assetMonitoring,
  onRefresh: pOnRefresh,
  useNewHeadlines = true,
}) => {
  const installationStart = installationStartDate ? toUserTZ(installationStartDate) : nowDateTime().sub(days(30))
  const endDate = installationEndDate ? toUserTZ(installationEndDate) : nowDateTime()

  const [graphs, setGraphs] = useState<Graph[]>(structuredClone(pGraphs) as Graph[])
  const [graphsVersion, setGraphsVersion] = useState<number>(0)
  const [figures, setFigures] = useState<Figure[]>(structuredClone(pFigures) as Figure[])
  const [refreshVersion, setRefreshVersion] = useState<number>(0)
  const [useBetaGraphs, setUseBetaGraphs] = useState<boolean>(false)
  const hasBetaGraphs = graphs.some((graph) => graph?.isBeta)

  const availableTimePeriods = Object.entries(TimePeriod).filter((entry) => entry[1] !== TimePeriod.ALL)

  const getPref = useGetPref()
  const setPref = useSetPref()
  const cTimePeriod = getPref("timePeriod")
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(
    cTimePeriod ? TimePeriod[cTimePeriod as keyof typeof TimePeriod] : TimePeriod.LastOneHour
  )
  const currentOption = mapToOption(timePeriod)

  const refreshGraphs = (graphs: Graph[]) =>
    (graphs as Graph[]).map((graph) => {
      const period = availableTimePeriods
        .filter(([key, value]) => value == timePeriod || key == timePeriod)
        .map(([_, value]) => value)[0] // Allow support for either key or value for the TimePeriod enum value

      let timeseries: Timeseries | undefined = structuredClone(TimePeriodMappings[period])
      if (period === TimePeriod.ALL) {
        const daysSinceinstallationStart =
          dateDiffInDays(installationStart, endDate) +
          (installationStart.hour() > 0 ? installationStart.hour() / 23 : 0)
        const noMonths = daysSinceinstallationStart / 30
        const noWeeks = daysSinceinstallationStart / 7
        const useMonths = noMonths >= 1
        const isFullDay = daysSinceinstallationStart >= 1
        const isFullWeek = noWeeks >= 1

        let scale = LAST_12_HOURS_SCALE
        if (daysSinceinstallationStart >= 1 || noWeeks >= 1) {
          scale = LAST_WEEK_SCALE
          if (noWeeks >= 2) {
            scale = LAST_WEEKS_SCALE
            if (noMonths >= 1) {
              scale = LAST_MONTH_SCALE
              if (noMonths >= 1.5) {
                scale = LAST_MONTHS_SCALE
              }
            }
          }
        }

        timeseries = {
          endResolution: { quantity: useMonths ? 6 : isFullWeek ? 1 : 30, resolution: isFullWeek ? "hour" : "minute" },
          startResolution: { quantity: isFullDay ? daysSinceinstallationStart : 1, resolution: "day" },
          interval: { quantity: useMonths ? 12 : isFullWeek ? 1 : 30, resolution: isFullWeek ? "hour" : "minute" },
          scale: scale,
        }
      }
      if (!graph.limits || !graph.limits.intervalMin) {
        if (!graph.limits) graph.limits = {}
        if (timeseries?.interval?.resolution !== "second")
          graph.limits.intervalMin = {"quantity": 5, "resolution": "minute"}
      }
      if (
        timeseries &&
        timeseries.interval &&
        graph.limits &&
        graph.limits.intervalMin
      ) {
        const intervalMS = rangeInMS(timeseries.interval.quantity, timeseries.interval.resolution)
        const intervalMinMS = rangeInMS(graph.limits.intervalMin.quantity, graph.limits.intervalMin.resolution)
        if (intervalMS < intervalMinMS) {
          // Clamp up: don't allow interval smaller than the minimum
          timeseries.interval = graph.limits.intervalMin
        } else if (intervalMinMS < intervalMS) {
          // Use finer granularity when the graph allows sub-minute and the time range is short enough
          const rangeMS = rangeInMS(timeseries.startResolution.quantity, timeseries.startResolution.resolution)
          const maxDataPoints = 720
          if (rangeMS / intervalMinMS <= maxDataPoints) {
            timeseries.interval = graph.limits.intervalMin
          }
        }
      }
        
      if (timeseries) {
        graph.timeseries = timeseries
        graph.source.range = timeseries.startResolution
      }

      return graph
    })

  useEffect(() => {
    const gs = refreshGraphs(structuredClone(pGraphs) as Graph[])
    setGraphs(gs)
    setGraphsVersion((v) => v + 1)
  }, [timePeriod, refreshVersion])

  useEffect(() => {
    if (pGraphs) {
      const gs = refreshGraphs(structuredClone(pGraphs) as Graph[])
      setGraphs(gs)
      setGraphsVersion((v) => v + 1)
    }
  }, [pGraphs])

  useEffect(() => {
    setFigures(pFigures as Figure[])
    setRefreshVersion(refreshVersion + 1)
  }, [pFigures])

  const onRefresh = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    setRefreshVersion(refreshVersion + 1)
    pOnRefresh?.()
  }

  const showFigures = (useNewHeadlines && installation) || (figures && installation)

  return (
    <>
      {/* Headline Figures Section */}
      <Box>
        {showFigures && useNewHeadlines && (
          <InstallationHeadlineFigures
            installationId={installation}
            stop={installationEndDate ? toUserTZ(installationEndDate) : undefined}
            refreshVersion={refreshVersion}
            figures={figures}
          />
        )}
        {showFigures && !useNewHeadlines && (
          <Figures
            ops={false}
            installationStartDate={installationStart}
            installationEndDate={endDate}
            installationId={installation}
            figures={figures}
            assetId={asset}
            key={refreshVersion}
            context={assetContext}
            monitoring={assetMonitoring}
          />
        )}
      </Box>

      {hasBetaGraphs && (
        <Box position="relative" mt={showFigures ? 5 : 0} mb={showFigures ? 5 : 0}>
          <AbsoluteCenter pl={1} pr={1} width="fit-content">
            <Link variant="primary" onClick={() => setUseBetaGraphs(!useBetaGraphs)} whiteSpace="nowrap">
              {useBetaGraphs ? "View system data" : "View asset data"}
            </Link>
          </AbsoluteCenter>
        </Box>
      )}

      {/* Controls Section */}
      <Flex
        justifyContent={{ base: "space-around", md: "space-between" }}
        width={{ base: "100%" }}
        flexDir={{ base: "column", md: "row" }}
        mt={5}
      >
        <HStack align="flex-end" marginBottom={4} flexWrap="wrap" gap={{ base: 2, md: 6 }}>
          <Flex className="period-select">
            <Field>
              <FieldLabel mt={2}>Select period:</FieldLabel>
              <NativeSelectRoot>
                <NativeSelectField
                  items={mapToOptions(availableTimePeriods)}
                  value={currentOption?.value}
                  onChange={(e) => {
                    e.preventDefault()
                    const value = e.target.value as TimePeriod
                    setTimePeriod(value)
                    setPref("timePeriod", value)
                  }}
                  className="period-select"
                />
              </NativeSelectRoot>
            </Field>
          </Flex>
          <Center height="40px">
            <Text whiteSpace="nowrap" fontSize="sm" textAlign="center">
              Timezone: {IANATimeZone()}
            </Text>
          </Center>
        </HStack>
        <Button variant={"hydrologiq_primary"} width={{ base: "100%", md: 140 }} onClick={onRefresh}>
          &#8635; Refresh
          <Text as="span" display={{ base: "none", md: "block" }}>
            &nbsp;data
          </Text>
        </Button>
      </Flex>

      {graphs && (
        <Graphs
          installationStartDate={installationStart}
          installationEndDate={endDate}
          installationId={installation}
          graphs={graphs.filter((graph) => (useBetaGraphs ? graph?.isBeta : !graph?.isBeta))}
          assetConfigs={assetConfigs}
          assetId={asset}
          key={graphsVersion}
        />
      )}
    </>
  )
}

export default InstallationMonitoringView
